import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Pusher from "pusher-js";
import { useUser } from "./UserContext";
import { debounce } from "lodash";

interface PusherContextType {
  message: any;
  setMessage: (message: any) => void;
}

const PusherContext = createContext<PusherContextType | undefined>(undefined);

export const PusherProvider = ({ children }: { children: ReactNode }) => {
  const [message, setMessage] = useState(null);
  const { userProfile } = useUser();
  const userId = userProfile?.user_id;

  // Create a reference to the Pusher instance
  const pusherRef = useRef<Pusher | null>(null);

  useEffect(() => {
    if (!pusherRef.current) {
      pusherRef.current = new Pusher(process.env.REACT_APP_PUSHER_KEY!, {
        cluster: process.env.REACT_APP_PUSHER_CLUSTER!,
        authEndpoint: process.env.REACT_APP_HAY2U_ENDPOINT + "/auth/pusher",
      });
    }

    const pusher = pusherRef.current;
    const userChannel = pusher.subscribe("HAY2U-PRIVATE");

    if (userId) {
      userChannel.bind(`HAY2U-${userId}`, (data: any) => {
        setMessage(data);
      });
    }

    return () => {
      userChannel.unbind_all();
      userChannel.unsubscribe();
    };
  }, [userId]);

  return (
    <PusherContext.Provider value={{ message, setMessage }}>
      {children}
    </PusherContext.Provider>
  );
};

export const usePusher = () => {
  const context = useContext(PusherContext);
  if (context === undefined) {
    throw new Error("usePusher must be used within a PusherProvider");
  }
  return context;
};
